import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, PartyPopper } from "lucide-react";
import { useApi, BUSINESS } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";

const STEPS = [
  { id: "clock_in", title: "Clock in on your first job", text: "Use the time clock below — pick the job, tap Clock in. Tap Clock out when you leave.", to: "/portal/time", cta: "Open time clock" },
  { id: "tasks", title: "Check your assigned tasks", text: "Move each task to In progress or Done as you work.", to: "/portal/tasks", cta: "See my tasks" },
  { id: "calendar", title: "Look at the job schedule", text: "Start dates, finish dates, task due dates and estimate visits.", to: "/portal/calendar", cta: "Open calendar" },
  { id: "update", title: "Post a progress update with a photo", text: "Open a project, write what got done, add job-site photos. The customer is emailed.", to: "/portal/projects", cta: "Go to projects" },
  { id: "office", title: "Save the office number", text: `Questions on site? Call or text ${BUSINESS.phone}.`, href: BUSINESS.phoneHref, cta: "Call the office" },
];

export const markSeen = (api, seen) => api("patch", "/onboarding", { seen }).catch(() => {});

export const WelcomeChecklist = () => {
  const api = useApi();
  const me = useMe();
  const [ob, setOb] = useState(null);
  useEffect(() => {
    api("get", "/onboarding").then(setOb).catch(() => {});
  }, [api]);
  if (!ob || ob.dismissed) return null;

  const done = STEPS.filter((s) => ob.steps[s.id]).length;
  const dismiss = () => {
    setOb({ ...ob, dismissed: true });
    api("patch", "/onboarding", { dismissed: true }).catch(() => {});
  };
  const tickOffice = () => {
    setOb({ ...ob, steps: { ...ob.steps, office: true } });
    markSeen(api, "office");
  };

  return (
    <div className="mb-8 overflow-hidden rounded-2xl border border-[#E6E2DA] bg-white" data-testid="welcome-checklist">
      <div className="flex items-start justify-between gap-4 bg-[#0E1F3F] p-5 text-white sm:p-6">
        <div>
          <p className="eyebrow !text-[#F2B91C]">Welcome to the crew</p>
          <h2 className="font-display mt-1 text-xl font-bold sm:text-2xl">Your first-day checklist, {me.name?.split(" ")[0] || "there"}</h2>
          <p className="mt-1 text-sm text-white/70" data-testid="checklist-progress">{done} of {STEPS.length} done</p>
        </div>
        <button onClick={dismiss} aria-label="Hide checklist" data-testid="checklist-dismiss-btn" className="rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><X size={18} /></button>
      </div>
      <div className="h-1.5 bg-[#EDF0F5]"><div className="h-full bg-[#F2B91C] transition-all duration-500" style={{ width: `${(done / STEPS.length) * 100}%` }} /></div>
      <ol className="divide-y">
        {STEPS.map((s) => {
          const ok = ob.steps[s.id];
          const linkCls = "shrink-0 rounded-full border border-[#E6E2DA] px-3 py-1.5 text-xs font-semibold hover:border-[#0E1F3F]";
          return (
            <li key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:px-6" data-testid={`checklist-step-${s.id}`}>
              <div className="flex flex-1 items-start gap-3">
                {ok ? <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-green-600" data-testid={`checklist-done-${s.id}`} /> : <Circle size={20} className="mt-0.5 shrink-0 text-[#C9C4BA]" />}
                <div><p className={`font-semibold ${ok ? "text-[#8C877D] line-through" : ""}`}>{s.title}</p><p className="text-sm text-[#5C5850]">{s.text}</p></div>
              </div>
              {!ok && (s.href
                ? <a href={s.href} onClick={tickOffice} className={linkCls} data-testid={`checklist-cta-${s.id}`}>{s.cta}</a>
                : <Link to={s.to} className={linkCls} data-testid={`checklist-cta-${s.id}`}>{s.cta}</Link>)}
              {!ok && s.id === "office" && <button onClick={tickOffice} className="text-xs font-semibold text-[#A67C00]" data-testid="checklist-office-saved-btn">I saved it</button>}
            </li>
          );
        })}
      </ol>
      {done === STEPS.length && (
        <div className="flex items-center justify-between gap-3 bg-green-50 px-6 py-4 text-sm font-semibold text-green-800" data-testid="checklist-complete">
          <span className="flex items-center gap-2"><PartyPopper size={18} />You're all set. Welcome aboard!</span>
          <button onClick={dismiss} className="rounded-full bg-green-700 px-4 py-1.5 text-xs text-white" data-testid="checklist-finish-btn">Hide checklist</button>
        </div>
      )}
    </div>
  );
};
